import { useEffect, useState, useMemo, useCallback } from 'react';
import { useApolloClient } from "./useApolloClient.js";
import { __use } from "./internal/index.js";
import { useSuspenseCache } from "./useSuspenseCache.js";
import { toApolloError, useTrackedQueryRefs, useWatchQueryOptions, } from "./useSuspenseQuery.js";
import { canonicalStringify } from "../../cache/index.js";
import { invariant, __DEV__ } from "../../utilities/globals/index.js";
export function useBackgroundQuery_experimental(query, options) {
    if (options === void 0) { options = Object.create(null); }
    var suspenseCache = useSuspenseCache(options.suspenseCache);
    var client = useApolloClient(options.client);
    var watchQueryOptions = useWatchQueryOptions({ query: query, options: options });
    var variables = watchQueryOptions.variables;
    var _a = options.queryKey, queryKey = _a === void 0 ? [] : _a;
    var cacheKey = [client, query, canonicalStringify(variables)].concat(queryKey);
    var queryRef = suspenseCache.getQueryRef(cacheKey, function () {
        return client.watchQuery(watchQueryOptions);
    });
    var _b = useState(function () { return new Map([[queryRef.key, queryRef.promise]]); }), promiseCache = _b[0], setPromiseCache = _b[1];
    useTrackedQueryRefs(queryRef);
    var fetchMore = useCallback(function (options) {
        var promise = queryRef.fetchMore(options);
        setPromiseCache(function (promiseCache) {
            return new Map(promiseCache).set(queryRef.key, promise);
        });
        return promise;
    }, [queryRef]);
    var refetch = useCallback(function (variables) {
        var promise = queryRef.refetch(variables);
        setPromiseCache(function (promiseCache) {
            return new Map(promiseCache).set(queryRef.key, promise);
        });
        return promise;
    }, [queryRef]);
    queryRef.promiseCache = promiseCache;
    return useMemo(function () {
        return [
            queryRef,
            {
                fetchMore: fetchMore,
                refetch: refetch,
            },
        ];
    }, [queryRef, fetchMore, refetch]);
}
export function useReadQuery_experimental(queryRef) {
    var _a = useState(0), forceUpdate = _a[1];
    __DEV__ ? invariant(queryRef.promiseCache, 'It appears that `useReadQuery` was used outside of `useBackgroundQuery`. ' +
        '`useReadQuery` is only supported for use with `useBackgroundQuery`. ' +
        'Please ensure you are passing the `queryRef` returned from `useBackgroundQuery`.') : invariant(queryRef.promiseCache, 34);
    var promise = queryRef.promiseCache.get(queryRef.key);
    if (!promise) {
        promise = queryRef.promise;
        queryRef.promiseCache.set(queryRef.key, promise);
    }
    useEffect(function () {
        return queryRef.listen(function (promise) {
            queryRef.promiseCache.set(queryRef.key, promise);
            forceUpdate(function (prevState) { return prevState + 1; });
        });
    }, [queryRef]);
    var result = __use(promise);
    return useMemo(function () {
        return {
            data: result.data,
            networkStatus: result.networkStatus,
            error: toApolloError(result),
        };
    }, [result]);
}
//# sourceMappingURL=useBackgroundQuery.js.map